import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Paperclip,
  ChevronDown,
  ChevronUp,
  X,
  Info,
  Calendar,
  ExternalLink
} from "lucide-react";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatusBadge({ status, children }) {
  const colors = {
    pass: "bg-green-100 text-green-800 border-green-300",
    fail: "bg-red-100 text-red-800 border-red-300",
    undecided: "bg-yellow-100 text-yellow-800 border-yellow-300",
    unknown: "bg-gray-100 text-gray-800 border-gray-300"
  };
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colors[status] || colors.unknown}`}>
      {children}
    </span>
  );
}

function CheckResult({ title, subtitle, status, explanation, details }) {
  const [expanded, setExpanded] = useState(false);
  
  const statusIcons = {
    pass: <CheckCircle className="w-5 h-5 text-green-600" />,
    fail: <XCircle className="w-5 h-5 text-red-600" />,
    undecided: <AlertCircle className="w-5 h-5 text-yellow-600" />,
    unknown: <AlertCircle className="w-5 h-5 text-gray-500" />
  };

  return (
    <div className="border-b border-gray-100 py-3 last:border-0">
      <div className="flex items-start gap-3">
        {statusIcons[status] || statusIcons.unknown}
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900">{title}</h4>
            <StatusBadge status={status}>
              {status === "pass" ? "Pass" : status === "fail" ? "Fail" : status === "undecided" ? "Undecided" : "Unknown"}
            </StatusBadge>
          </div>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-0.5 italic">{subtitle}</p>
          )}
          <p className="text-sm text-gray-600 mt-1">{explanation}</p>
          {details && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-accent hover:underline mt-1 flex items-center gap-1"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? "Hide details" : "Show details"}
            </button>
          )}
          {expanded && details && (
            <p className="text-xs text-gray-500 mt-2 bg-gray-50 p-2 rounded">{details}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ArticleResults({ article, index }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Check if article passes all criteria (all 5 checks must pass)
  const checks = article.checks || {};
  const allChecks = [
    checks.publication_reliability,
    checks.mainstream_national,
    checks.primary_topic,
    checks.in_depth,
    checks.independence
  ];
  
  const passCount = allChecks.filter(c => c?.status === "pass").length;
  const hasAnyFail = allChecks.some(c => c?.status === "fail");
  const articlePasses = !hasAnyFail && passCount === 5;
  const hasError = article.hasError || false;
  
  return (
    <Card className={`bg-white ${hasError ? "border-amber-400" : articlePasses ? "border-green-300" : "border-red-300"} border-2`}>
      <CardHeader 
        className="pb-2 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${hasError ? "bg-amber-100" : articlePasses ? "bg-green-100" : "bg-red-100"}`}>
              <span className={`font-bold text-sm ${hasError ? "text-amber-700" : articlePasses ? "text-green-700" : "text-red-700"}`}>{index}</span>
            </div>
            <div>
              <span className="text-gray-900">Article {index}</span>
              {article.publication && article.publication !== "(missing)" && (
                <span className="text-gray-500 font-normal ml-2">— {article.publication}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-semibold ${hasError ? "bg-amber-100 text-amber-800" : articlePasses ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
              {hasError ? "⚠ INCOMPLETE" : articlePasses ? "✓ PASSES" : "✗ FAILS"}
            </div>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </CardTitle>
        {hasError && article.errorMessage && (
          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {article.errorMessage}
            </p>
          </div>
        )}
        {!hasError && hasAnyFail && !isExpanded && (
          <p className="text-sm text-red-600 mt-2">
            This article fails one or more criteria. Click to see details.
          </p>
        )}
        {!hasError && !isExpanded && (
          <p className="text-sm text-gray-500 mt-2">
            Click to {isExpanded ? "hide" : "view"} detailed analysis
          </p>
        )}
      </CardHeader>
      {isExpanded && (
        <CardContent className="space-y-1 border-t border-gray-100 pt-4">
          <CheckResult
            title="1. Publication Authority"
            subtitle="Wikipedia can only use news articles it believes has a &quot;reputation for fact checking.&quot;"
            status={article.checks?.publication_reliability?.status || "unknown"}
            explanation={article.checks?.publication_reliability?.explanation || "Unable to determine"}
            details={article.checks?.publication_reliability?.details}
          />
          <CheckResult
            title="2. Mainstream National Publication"
            subtitle="To qualify for a page, there has to be news articles in mainstream publications, not just local or trade press."
            status={article.checks?.mainstream_national?.status || "unknown"}
            explanation={article.checks?.mainstream_national?.explanation || "Unable to determine"}
            details={article.checks?.mainstream_national?.details}
          />
          <CheckResult
            title="3. Primary Focus of the Article"
            subtitle="The article has to be mainly focused on your topic."
            status={article.checks?.primary_topic?.status || "unknown"}
            explanation={article.checks?.primary_topic?.explanation || "Unable to determine"}
            details={article.checks?.primary_topic?.details}
          />
          <CheckResult
            title="4. In-Depth Profile"
            subtitle="The article has to be a lengthy piece focused on your topic, not a blurb, mention, or routine announcement."
            status={article.checks?.in_depth?.status || "unknown"}
            explanation={article.checks?.in_depth?.explanation || "Unable to determine"}
            details={article.checks?.in_depth?.details}
          />
          <CheckResult
            title="5. Independence"
            status={article.checks?.independence?.status || "unknown"}
            explanation={article.checks?.independence?.explanation || "Unable to determine"}
            details={article.checks?.independence?.details}
          />
        </CardContent>
      )}
    </Card>
  );
}

function ArticleInput({ index, article, onUpdate, isLoading }) {
  const fileInputRef = useRef(null);
  const [isFetching, setIsFetching] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const debounceRef = useRef(null);

  const fetchContent = useCallback(async (url) => {
    if (!url) return;
    
    setIsFetching(true);
    try {
      const response = await fetch(`${API}/notability/fetch-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url })
      });
      
      const data = await response.json();
      
      if (data.success) {
        const hasContent = data.content && data.content.trim().length > 0;
        const hasPublication = data.publication && data.publication.trim().length > 0;
        
        onUpdate({ 
          ...article, 
          url: url, // Keep the URL
          content: data.content || "",
          publication: data.publication || "",
          fetched: true,
          error: null,
          needsManualEntry: !hasContent || !hasPublication,
          missingContent: !hasContent,
          missingPublication: !hasPublication
        });
      } else {
        onUpdate({ 
          ...article, 
          url: url, // Keep the URL even on error
          fetched: true, // Mark as fetched so we can still submit
          content: "",
          publication: "",
          error: data.error || "Could not access article.",
          needsManualEntry: true,
          missingContent: true,
          missingPublication: true
        });
      }
    } catch (error) {
      onUpdate({ 
        ...article, 
        url: url, // Keep the URL
        fetched: true, // Mark as fetched so we can still submit
        content: "",
        publication: "",
        error: "Failed to fetch article.",
        needsManualEntry: true,
        missingContent: true,
        missingPublication: true
      });
    } finally {
      setIsFetching(false);
    }
  }, [article, onUpdate]);

  const handleUrlChange = (url) => {
    onUpdate({ ...article, url, fetched: false, error: null });
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    if (url && url.startsWith('http')) {
      debounceRef.current = setTimeout(() => {
        fetchContent(url);
      }, 1000);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a PDF or Word document");
      return;
    }

    setIsFetching(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API}/notability/extract-file`, {
        method: "POST",
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        const hasContent = data.content && data.content.trim().length > 0;
        const hasPublication = data.publication && data.publication.trim().length > 0;
        
        onUpdate({ 
          ...article, 
          url: file.name,
          content: data.content || "",
          publication: data.publication || "",
          fetched: true,
          error: null,
          isFile: true,
          needsManualEntry: !hasContent || !hasPublication,
          missingContent: !hasContent,
          missingPublication: !hasPublication
        });
        
        if (!hasContent || !hasPublication) {
          toast.info("File loaded. Please add missing information manually.");
        } else {
          toast.success("File loaded successfully");
        }
      } else {
        onUpdate({
          ...article,
          url: file.name,
          content: "",
          publication: "",
          fetched: true,
          error: null,
          isFile: true,
          needsManualEntry: true,
          missingContent: true,
          missingPublication: true
        });
        toast.info("File attached. Please add publication and content manually.");
      }
    } catch (error) {
      toast.error("Failed to process file");
    } finally {
      setIsFetching(false);
    }
  };

  const clearArticle = () => {
    onUpdate({
      ...article,
      url: "",
      content: "",
      publication: "",
      fetched: false,
      error: null,
      isFile: false
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Increase preview length by 50% (from 100 to 150 characters)
  const contentPreview = article.content 
    ? (article.content.length > 150 ? article.content.substring(0, 150) + "..." : article.content)
    : "";

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
          <span className="font-bold text-black text-sm">{index}</span>
        </div>
        <Label className="text-gray-700 font-medium">Article {index}</Label>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-1">
          <Label className="text-sm text-gray-600">Article URL</Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type="text"
                placeholder="https://nytimes.com/article... or attach file"
                value={article.url || ""}
                onChange={(e) => handleUrlChange(e.target.value)}
                disabled={isLoading || isFetching}
                className="bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent pr-10"
              />
              {(article.url || article.fetched) && (
                <button
                  type="button"
                  onClick={clearArticle}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading || isFetching}
              className="shrink-0 border-gray-300 text-gray-600 hover:text-gray-900"
              title="Attach file"
            >
              <Paperclip className="w-4 h-4" />
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
          
          {isFetching && (
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading article...
            </p>
          )}
          
          {article.error && (
            <p className="text-sm text-amber-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {article.error}
            </p>
          )}
          
          {article.needsManualEntry && !article.error && (
            <p className="text-sm text-blue-600 flex items-center gap-1">
              <Info className="w-3 h-3" />
              {article.missingContent && article.missingPublication 
                ? "Could not extract content. Please add publication and content manually below."
                : article.missingPublication 
                  ? "Please add publication name manually below."
                  : "Please add article content manually below."}
            </p>
          )}
        </div>

        {(article.fetched || article.url) && (
          <div className="space-y-4 pt-4 border-t border-gray-100">
            <div className="space-y-1">
              <Label className="text-sm text-gray-600">
                Publication
                {article.missingPublication && <span className="text-amber-600 ml-1">*</span>}
              </Label>
              <Input
                type="text"
                placeholder="e.g., The New York Times"
                value={article.publication || ""}
                onChange={(e) => onUpdate({ ...article, publication: e.target.value, missingPublication: !e.target.value })}
                disabled={isLoading}
                className={`bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent ${article.missingPublication ? 'border-amber-400' : ''}`}
              />
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-sm text-gray-600">
                  Article Content
                  {article.missingContent && <span className="text-amber-600 ml-1">*</span>}
                </Label>
                <button
                  type="button"
                  onClick={() => setExpanded(!expanded)}
                  className="text-sm text-accent hover:text-accent/80 flex items-center gap-1"
                >
                  {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {expanded ? "Collapse" : "View/Edit"}
                </button>
              </div>
              
              {expanded ? (
                <Textarea
                  value={article.content || ""}
                  onChange={(e) => onUpdate({ ...article, content: e.target.value, missingContent: !e.target.value })}
                  disabled={isLoading}
                  rows={8}
                  className={`bg-white border-gray-300 text-gray-900 text-sm focus:border-accent focus:ring-accent ${article.missingContent ? 'border-amber-400' : ''}`}
                />
              ) : (
                <div className={`bg-gray-50 rounded-md p-3 text-sm text-gray-600 border ${article.missingContent ? 'border-amber-400' : 'border-gray-200'}`}>
                  {contentPreview || <span className="italic text-amber-500">No content - click View/Edit to add manually</span>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function NotabilityChecker() {
  // Check for preview mode via URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const previewMode = urlParams.get('preview') === 'not-qualified';
  
  const [step, setStep] = useState(previewMode ? 3 : 1);
  const [initialChoice, setInitialChoice] = useState(previewMode ? "check" : null); // "check" or "consult"
  const [topic, setTopic] = useState(previewMode ? "Example Company Inc." : "");
  const [alreadyHasPage, setAlreadyHasPage] = useState(false);
  const [existingPageUrl, setExistingPageUrl] = useState("");
  const [articles, setArticles] = useState([
    { id: 1, url: "", content: "", publication: "", fetched: false, error: null },
    { id: 2, url: "", content: "", publication: "", fetched: false, error: null },
    { id: 3, url: "", content: "", publication: "", fetched: false, error: null }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(previewMode ? {
    qualifies: false,
    passingArticles: 1,
    hasExistingWikipediaPage: false,
    articles: [
      {
        index: 1,
        url: "https://example.com/article1",
        passes: false,
        checks: {
          publication_reliability: { status: "pass", explanation: "The source appears to be a reliable publication." },
          mainstream_national: { status: "fail", explanation: "This appears to be a local or trade publication, not a mainstream national source." },
          primary_topic: { status: "pass", explanation: "The article is primarily focused on the topic." },
          in_depth: { status: "fail", explanation: "The article is a brief mention rather than an in-depth profile." },
          independence: { status: "pass", explanation: "The source appears to be editorially independent." }
        }
      },
      {
        index: 2,
        url: "https://example.com/article2",
        passes: true,
        checks: {
          publication_reliability: { status: "pass", explanation: "The source appears to be a reliable publication." },
          mainstream_national: { status: "pass", explanation: "This is a mainstream national publication." },
          primary_topic: { status: "pass", explanation: "The article is primarily focused on the topic." },
          in_depth: { status: "pass", explanation: "The article provides substantial coverage of the topic." },
          independence: { status: "pass", explanation: "The source appears to be editorially independent." }
        }
      }
    ]
  } : null);

  // Calculate total steps based on choice
  const getTotalSteps = () => {
    if (initialChoice === "consult") return 1; // Just show calendar
    return 4; // Full flow: choice -> criteria -> articles -> results -> calendar
  };
  
  const totalSteps = getTotalSteps();
  const progress = (step / totalSteps) * 100;

  const updateArticle = (index, data) => {
    const newArticles = [...articles];
    newArticles[index] = { ...newArticles[index], ...data };
    setArticles(newArticles);
  };

  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!topic.trim()) {
      toast.error("Please enter the Wikipedia page topic");
      return;
    }

    // Include articles that have a URL/file even if missing content/publication
    const articlesToSubmit = articles.map((a, i) => ({
      index: i + 1,
      content: a.content || "",
      publication: a.publication || "",
      url: a.url || "",
      hasUrl: !!(a.url && a.url.trim()),
      missingContent: !a.content || !a.content.trim(),
      missingPublication: !a.publication || !a.publication.trim()
    })).filter(a => a.hasUrl || (a.content && a.publication));
    
    if (articlesToSubmit.length === 0) {
      toast.error("Please provide at least one article");
      return;
    }

    setIsLoading(true);
    setResults(null);

    try {
      const response = await fetch(`${API}/notability/check-detailed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topic,
          articles: articlesToSubmit
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to analyze articles");
      }

      const data = await response.json();
      
      // Calculate passing articles (all 5 checks must pass)
      const articlesWithPassStatus = data.articles.map(article => {
        const checks = article.checks || {};
        const allChecks = [
          checks.publication_reliability,
          checks.mainstream_national,
          checks.primary_topic,
          checks.in_depth,
          checks.independence
        ];
        const hasAnyFail = allChecks.some(c => c?.status === "fail");
        const passCount = allChecks.filter(c => c?.status === "pass").length;
        return {
          ...article,
          articlePasses: !hasAnyFail && passCount === 5
        };
      });
      
      const passingArticles = articlesWithPassStatus.filter(a => a.articlePasses).length;
      
      setResults({
        ...data,
        articles: articlesWithPassStatus,
        passingArticles,
        qualifies: passingArticles >= 2,
        hasExistingWikipediaPage: data.hasExistingWikipediaPage || false
      });
      setStep(3);
      toast.success("Analysis complete!");
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Failed to analyze articles");
    } finally {
      setIsLoading(false);
    }
  };

  // Cal.com embed loads via iframe, no external script needed

  const handleInitialChoice = (choice) => {
    setInitialChoice(choice);
    if (choice === "check") {
      setStep(1); // Stay on step 1 to show criteria
    }
  };

  const resetWizard = () => {
    setStep(1);
    setInitialChoice(null);
    setTopic("");
    setArticles([
      { id: 1, url: "", content: "", publication: "", fetched: false, error: null },
      { id: 2, url: "", content: "", publication: "", fetched: false, error: null },
      { id: 3, url: "", content: "", publication: "", fetched: false, error: null }
    ]);
    setResults(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-6">
      <SEO 
        title="Wikipedia Notability Checker - Free Assessment Tool"
        description="Check if your topic meets Wikipedia's notability guidelines with our free AI-powered assessment tool. Get expert analysis of your Wikipedia eligibility."
        canonical="/notability"
      />
      <div className="max-w-4xl mx-auto mb-4">
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-accent mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
        
        <div className="flex items-center gap-4 mb-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_df6f992c-82a6-44fb-a88f-450c0ac99b5b/artifacts/cvze4lab_q2y0upqj_75c9mfk0_Square%20Logo%20%28100%20x%20100%29.png" 
            alt="Ethical Wiki" 
            className="w-10 h-10"
            width="40"
            height="40"
          />
          <div>
            <h1 className="font-heading text-2xl text-gray-900">Wikipedia Assessment</h1>
            <p className="text-gray-600 text-sm">Get expert Wikipedia advice</p>
          </div>
        </div>
        
        <Progress value={progress} className="h-1 bg-gray-200" />
        <p className="text-gray-500 text-sm mt-2">
          {initialChoice === "consult" ? "Schedule Consultation" : `Step ${step} of ${totalSteps}`}
        </p>
      </div>

      <div className="max-w-4xl mx-auto">
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-8">
            {/* Step 1: Initial Choice OR Criteria Explanation */}
            {step === 1 && !initialChoice && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CardHeader className="p-0 mb-8">
                  <CardTitle className="font-heading text-2xl text-gray-900">How Can We Help?</CardTitle>
                </CardHeader>
                
                <div className="space-y-4">
                  {/* Recommended Option */}
                  <div 
                    className="border-2 border-accent bg-accent/5 p-6 rounded-lg cursor-pointer hover:bg-accent/10 transition-colors"
                    onClick={() => handleInitialChoice("check")}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-black" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">Check If Your Topic Qualifies</h3>
                          <span className="text-xs bg-accent text-black px-2 py-0.5 rounded-full font-medium">Recommended</span>
                        </div>
                        <p className="text-gray-600 text-sm">
                          Use our AI wizard to see if your topic qualifies for a Wikipedia page. Recommended before a consultation, even if a page already exists.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Consultation Option */}
                  <div 
                    className="border-2 border-gray-200 p-6 rounded-lg cursor-pointer hover:border-gray-300 hover:bg-gray-50 transition-colors"
                    onClick={() => handleInitialChoice("consult")}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">Schedule an Initial Consultation</h3>
                        <p className="text-gray-600 text-sm">
                          Purchase and schedule a 30-minute initial consultation with Ethical Wiki to discuss your Wikipedia presence.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 1 with choice made: Show Criteria */}
            {step === 1 && initialChoice === "check" && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CardHeader className="p-0 mb-8">
                  <CardTitle className="font-heading text-2xl text-gray-900">Understand the Criteria</CardTitle>
                </CardHeader>
                
                <div className="space-y-6">
                  <div>
                    <p className="text-gray-700 mb-6 text-base leading-relaxed">
                      To qualify for a Wikipedia page, your topic must have at least two news articles that meet <strong>all four</strong> of the following criteria:
                    </p>
                    <ol className="space-y-4">
                      <li className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-white font-bold text-sm">1</span>
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">An in-depth profile</p>
                          <p className="text-gray-600 text-sm leading-relaxed">The article should infer independent editorial judgment and fact-checking. No brief mentions or blurbs, or routine announcements.</p>
                        </div>
                      </li>
                      <li className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-white font-bold text-sm">2</span>
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">Mainstream national media</p>
                          <p className="text-gray-600 text-sm leading-relaxed">From a publication with a broad, general audience and national reach</p>
                        </div>
                      </li>
                      <li className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-white font-bold text-sm">3</span>
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">Written by an independent journalist</p>
                          <p className="text-gray-600 text-sm leading-relaxed">No press releases, sponsored content, Forbes contributors, or Q&A interviews</p>
                        </div>
                      </li>
                      <li className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-white font-bold text-sm">4</span>
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">Your topic is the main focus</p>
                          <p className="text-gray-600 text-sm leading-relaxed">The article is primarily about your topic, not merely your topic's opinion, or tips about something else.</p>
                        </div>
                      </li>
                    </ol>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Consultation Calendar (when chosen from initial choice) */}
            {step === 1 && initialChoice === "consult" && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CardHeader className="p-0 mb-8">
                  <CardTitle className="font-heading text-2xl text-gray-900">Schedule Your Consultation</CardTitle>
                  <CardDescription className="text-gray-600">
                    Book a session with Ethical Wiki for a professional evaluation
                  </CardDescription>
                </CardHeader>
                
                <div className="space-y-6">
                  {/* Cal.com Embed */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <iframe
                      src="https://cal.com/ethicalwiki/ew-new-page-consult?embed=true&hideBranding=true"
                      width="100%"
                      height="600"
                      frameBorder="0"
                      title="Schedule a consultation"
                      className="w-full"
                    />
                  </div>

                  <div className="text-center text-gray-500 text-sm">
                    <p>Having trouble with the calendar? <a href="https://cal.com/ethicalwiki/ew-new-page-consult" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Open in new tab</a></p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Article Input */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CardHeader className="p-0 mb-8">
                  <CardTitle className="font-heading text-2xl text-gray-900">Submit Your Articles</CardTitle>
                  <CardDescription className="text-gray-600">
                    Provide the topic and three supporting articles for analysis
                  </CardDescription>
                </CardHeader>
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="topic" className="text-gray-700">Wikipedia Page Topic *</Label>
                    <Input
                      id="topic"
                      type="text"
                      placeholder="e.g., John Smith (entrepreneur), Acme Corporation"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      disabled={isLoading}
                      className="mt-2 bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent"
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="alreadyHasPage"
                        checked={alreadyHasPage}
                        onChange={(e) => setAlreadyHasPage(e.target.checked)}
                        disabled={isLoading}
                        className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
                      />
                      <Label htmlFor="alreadyHasPage" className="text-gray-700 cursor-pointer">
                        Already has a Wikipedia page
                      </Label>
                    </div>
                    
                    {alreadyHasPage && (
                      <div className="ml-6">
                        <Label htmlFor="existingPageUrl" className="text-sm text-gray-600">Link to existing Wikipedia page</Label>
                        <Input
                          id="existingPageUrl"
                          type="text"
                          placeholder="https://en.wikipedia.org/wiki/..."
                          value={existingPageUrl}
                          onChange={(e) => setExistingPageUrl(e.target.value)}
                          disabled={isLoading}
                          className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <Label className="text-gray-700">
                      Supporting Articles *
                      <span className="font-normal text-gray-500 ml-2">
                        (provide three in-depth profiles)
                      </span>
                    </Label>
                    
                    {articles.map((article, index) => (
                      <ArticleInput
                        key={article.id}
                        index={index + 1}
                        article={article}
                        onUpdate={(data) => updateArticle(index, data)}
                        isLoading={isLoading}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Results */}
            {step === 3 && results && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CardHeader className="p-0 mb-8">
                  <CardTitle className="font-heading text-2xl text-gray-900">Analysis Results</CardTitle>
                </CardHeader>

                <div className="space-y-6">
                  {/* Overall Result */}
                  <div className={`p-6 rounded-lg border-2 ${results.qualifies ? "bg-green-50 border-green-300" : "bg-red-50 border-red-300"}`}>
                    <div className="flex items-center gap-3 mb-3">
                      {results.qualifies ? (
                        <CheckCircle className="w-8 h-8 text-green-600" />
                      ) : (
                        <XCircle className="w-8 h-8 text-red-600" />
                      )}
                      <h3 className={`font-heading text-xl ${results.qualifies ? "text-green-800" : "text-red-800"}`}>
                        {results.qualifies ? "Topic May Qualify for Wikipedia" : "Topic May Not Qualify Yet"}
                      </h3>
                    </div>
                    <p className={`${results.qualifies ? "text-green-700" : "text-red-700"}`}>
                      <strong>{results.passingArticles} of {results.articles?.length || 0}</strong> articles passed all criteria.
                      {results.qualifies 
                        ? " With two or more qualifying articles, your topic may meet Wikipedia's notability requirements."
                        : " You need at least 2 articles that passed all criteria."}
                    </p>
                  </div>

                  {/* Individual Article Results */}
                  {results.articles && results.articles.map((article, index) => (
                    <ArticleResults
                      key={index}
                      article={article}
                      index={article.index || index + 1}
                    />
                  ))}

                  {/* Contextual Message Based on Results */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <p className="text-gray-700 leading-relaxed">
                      {results.qualifies ? (
                        "The AI evaluator believes your topic may meet Wikipedia's criteria for a page. Schedule a consultation with a human to assess further."
                      ) : (alreadyHasPage || results.hasExistingWikipediaPage) ? (
                        "Having a Wikipedia page already doesn't mean the page actually meets Wikipedia's criteria. Many pages are deleted after staying up for years."
                      ) : (
                        "In most cases, Ethical Wiki advises against attempting to create a new Wikipedia page. Editors are pretty reliable about starting one on topics famous enough. Schedule a consultation if you'd like to discuss further."
                      )}
                    </p>
                  </div>
                  
                  {/* Handshake Wiki Alternative - shown when topic doesn't qualify */}
                  {!results.qualifies && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <ExternalLink className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-blue-900 mb-2">Consider Handshake Wiki</h4>
                          <p className="text-blue-800 text-sm leading-relaxed mb-3">
                            If your topic doesn't yet meet Wikipedia's strict notability requirements, <strong>Handshake Wiki</strong> offers an alternative wiki focused on business topics.
                          </p>
                          <a 
                            href="https://handshakewiki.com" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
                          >
                            Visit handshakewiki.com
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 4: Schedule Consultation */}
            {step === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CardHeader className="p-0 mb-8">
                  <CardTitle className="font-heading text-2xl text-gray-900">Schedule a Consultation</CardTitle>
                  <CardDescription className="text-gray-600">
                    Get a professional assessment from a Wikipedia expert
                  </CardDescription>
                </CardHeader>
                
                <div className="space-y-4 px-4">
                  {/* Cal.com Embed with pre-filled data */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <iframe
                      src={(() => {
                        // Build the cal.com URL with pre-filled fields
                        const baseUrl = "https://cal.com/ethicalwiki/ew-new-page-consult";
                        const params = new URLSearchParams();
                        params.set("embed", "true");
                        params.set("hideBranding", "true");
                        
                        // Pre-fill Wikipedia Page field with topic
                        if (topic) {
                          params.set("Wikipedia-Page", topic);
                        }
                        
                        // Build notes with article links and AI summary
                        let notes = "";
                        
                        // Add article links
                        const articleLinks = articles
                          .filter(a => a.url)
                          .map((a, i) => `Article ${i + 1}: ${a.url}`)
                          .join("\n");
                        
                        if (articleLinks) {
                          notes += "Article Links:\n" + articleLinks + "\n\n";
                        }
                        
                        // Add AI recommendation summary
                        if (results) {
                          notes += "AI Assessment Summary:\n";
                          notes += `- ${results.passingArticles || 0} of ${results.articles?.length || 0} articles passed all criteria\n`;
                          notes += `- Recommendation: ${results.recommendation || "N/A"}\n`;
                          if (alreadyHasPage && existingPageUrl) {
                            notes += `- Existing Wikipedia page: ${existingPageUrl}\n`;
                          }
                        }
                        
                        if (notes) {
                          params.set("Notes", notes);
                        }
                        
                        return `${baseUrl}?${params.toString()}`;
                      })()}
                      width="100%"
                      height="600"
                      frameBorder="0"
                      title="Schedule a consultation"
                      className="w-full"
                    />
                  </div>

                  <div className="text-center text-gray-500 text-sm">
                    <p>Having trouble with the calendar? <a href="https://cal.com/ethicalwiki/ew-new-page-consult" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Open in new tab</a></p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              {/* Back/Previous Button */}
              {step === 1 && !initialChoice && (
                <div /> /* Empty div for spacing when no back button */
              )}
              
              {step === 1 && initialChoice === "check" && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setInitialChoice(null)}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
              
              {step === 1 && initialChoice === "consult" && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setInitialChoice(null)}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
              
              {step === 2 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
              )}
              
              {step === 3 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Edit
                </Button>
              )}
              
              {step === 4 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetWizard}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Start Over
                </Button>
              )}
              
              {/* Forward/Action Buttons */}
              {step === 1 && !initialChoice && (
                <div /> /* No forward button on initial choice screen */
              )}
              
              {step === 1 && initialChoice === "check" && (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="bg-accent text-black hover:bg-accent/90"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              
              {step === 1 && initialChoice === "consult" && (
                <div /> /* No forward button on consultation screen */
              )}
              
              {step === 2 && (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="bg-accent text-black hover:bg-accent/90"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      Check Notability
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
              
              {step === 3 && (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="bg-accent text-black hover:bg-accent/90"
                >
                  Schedule Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              
              {step === 4 && (
                <div /> /* Start Over button is on the left */
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
